/**
 * Горячие клавиши: сочетания, их запись, разбор и подписи. Без сторов и окна — эти функции
 * проверяет web/scripts/navigation.test.mjs. Клавиша хранится как KeyboardEvent.code: так
 * сочетание не зависит от раскладки (Ctrl+K на русской раскладке — тоже KeyK), и тот же код
 * понимает глобальный перехват в приложении для компьютера (desktop/src/keys.ts).
 * Кнопка мыши лежит в том же поле как Mouse1…Mouse5 — старые сохранённые сочетания не меняются.
 */

export type HotkeyAction = 'toggleMute' | 'toggleDeafen' | 'quickSwitcher' | 'toggleOverlay'

export interface Combo {
  code: string
  ctrl: boolean
  alt: boolean
  shift: boolean
  meta: boolean
}

/** Что можно назначить: подпись, раздел и работает ли в фоне (в приложении для компьютера). */
export const HOTKEY_ACTIONS: { id: HotkeyAction; label: string; group: 'voice' | 'navigation'; global: boolean }[] = [
  { id: 'toggleMute', label: 'Включить или выключить микрофон', group: 'voice', global: true },
  { id: 'toggleDeafen', label: 'Включить или выключить звук', group: 'voice', global: true },
  { id: 'toggleOverlay', label: 'Показать или скрыть оверлей', group: 'voice', global: true },
  { id: 'quickSwitcher', label: 'Быстрый переход', group: 'navigation', global: false },
]

/** Назначения по умолчанию — как у Discord: на маке вместо Ctrl — Cmd. */
export function defaultCombo(action: HotkeyAction, mac: boolean): Combo {
  const mod = { ctrl: !mac, alt: false, meta: mac }
  switch (action) {
    case 'toggleMute':
      return { code: 'KeyM', shift: true, ...mod }
    case 'toggleDeafen':
      return { code: 'KeyD', shift: true, ...mod }
    case 'toggleOverlay':
      return { code: 'KeyO', shift: true, ...mod }
    case 'quickSwitcher':
      return { code: 'KeyK', shift: false, ...mod }
  }
}

const MODIFIER_CODES = new Set(['ControlLeft', 'ControlRight', 'ShiftLeft', 'ShiftRight', 'AltLeft', 'AltRight', 'MetaLeft', 'MetaRight', 'OSLeft', 'OSRight', 'CapsLock', 'Fn', 'FnLock'])

interface KeyLike {
  code: string
  ctrlKey: boolean
  altKey: boolean
  shiftKey: boolean
  metaKey: boolean
}

/** Сочетание из нажатия; одни модификаторы (человек ещё держит Ctrl) — null: ждём основную клавишу. */
export function comboFromEvent(e: KeyLike): Combo | null {
  if (!e.code || MODIFIER_CODES.has(e.code)) return null
  return { code: e.code, ctrl: e.ctrlKey, alt: e.altKey, shift: e.shiftKey, meta: e.metaKey }
}

export function sameCombo(a: Combo | null | undefined, b: Combo | null | undefined): boolean {
  if (!a || !b) return a === b
  return a.code === b.code && a.ctrl === b.ctrl && a.alt === b.alt && a.shift === b.shift && a.meta === b.meta
}

/** Нажатие — ровно это сочетание: лишний модификатор значит другое сочетание. */
export function matchesEvent(combo: Combo, e: KeyLike): boolean {
  return e.code === combo.code && e.ctrlKey === combo.ctrl && e.altKey === combo.alt && e.shiftKey === combo.shift && e.metaKey === combo.meta
}

/*
 * Кнопки мыши называем, как в играх: Mouse1 — левая, Mouse2 — правая, Mouse3 — средняя,
 * Mouse4 и Mouse5 — боковые «назад» и «вперёд». Номер N совпадает с кнопкой uiohook в приложении
 * для компьютера, а MouseEvent.button считает по-своему: 0 левая, 1 средняя, 2 правая, 3 и 4 боковые.
 */
const MOUSE_CODES = ['Mouse1', 'Mouse3', 'Mouse2', 'Mouse4', 'Mouse5']

interface MouseLike {
  button: number
  ctrlKey: boolean
  altKey: boolean
  shiftKey: boolean
  metaKey: boolean
}

/** Код кнопки по MouseEvent.button; null — кнопка, которой у нас нет названия. */
export function mouseCode(button: number): string | null {
  return MOUSE_CODES[button] ?? null
}

/** MouseEvent.button по коду Mouse1…Mouse5; -1 — это не кнопка мыши. */
export function mouseButtonOf(code: string): number {
  return MOUSE_CODES.indexOf(code)
}

export function isMouseCode(code: string): boolean {
  return MOUSE_CODES.includes(code)
}

const hasModifier = (c: Pick<Combo, 'ctrl' | 'alt' | 'shift' | 'meta'>) => c.ctrl || c.alt || c.shift || c.meta

/** Левая и правая без модификатора заняты кликом и меню: такое сочетание не даст пользоваться мышью. */
export function usableCombo(combo: Combo): boolean {
  return !(combo.code === 'Mouse1' || combo.code === 'Mouse2') || hasModifier(combo)
}

/** Сочетание из нажатия кнопки мыши; обычный клик левой или правой — null, им пользуются по назначению. */
export function comboFromMouse(e: MouseLike): Combo | null {
  const code = mouseCode(e.button)
  if (!code) return null
  const combo = { code, ctrl: e.ctrlKey, alt: e.altKey, shift: e.shiftKey, meta: e.metaKey }
  return usableCombo(combo) ? combo : null
}

/** Нажатие кнопки мыши — ровно это сочетание, модификаторы тоже. */
export function matchesMouse(combo: Combo, e: MouseLike): boolean {
  return mouseCode(e.button) === combo.code && e.ctrlKey === combo.ctrl && e.altKey === combo.alt && e.shiftKey === combo.shift && e.metaKey === combo.meta
}

const NON_TEXT_KEYS = new Set(['Pause', 'ScrollLock', 'PrintScreen', 'Insert', 'ContextMenu'])

/**
 * Можно ли сработать, пока курсор в поле ввода. С Ctrl, Alt или Cmd — да: так текст не набирают
 * (Ctrl+K открывает быстрый переход и из поля сообщения, как у Discord); F-клавиши и
 * мультимедийные тоже ничего не печатают. Одиночная буква или Shift+буква — это набор текста,
 * их в полях не перехватываем. Кнопкой мыши текст не набирают вовсе.
 */
export function worksInInputs(combo: Combo): boolean {
  if (combo.ctrl || combo.alt || combo.meta || isMouseCode(combo.code)) return true
  return /^F\d{1,2}$/.test(combo.code) || /^(Media|Audio|Launch|Browser)/.test(combo.code) || NON_TEXT_KEYS.has(combo.code)
}

const KEY_NAMES: Record<string, string> = {
  Space: 'Пробел',
  Enter: 'Enter',
  Tab: 'Tab',
  Backspace: 'Backspace',
  Delete: 'Delete',
  Escape: 'Esc',
  Backquote: '`',
  Minus: '-',
  Equal: '=',
  BracketLeft: '[',
  BracketRight: ']',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Comma: ',',
  Period: '.',
  Slash: '/',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  PageUp: 'Page Up',
  PageDown: 'Page Down',
  Insert: 'Insert',
  Home: 'Home',
  End: 'End',
  // боковые — номерами, как их подписывают в играх и в настройках рации
  Mouse1: 'Левая кнопка мыши',
  Mouse2: 'Правая кнопка мыши',
  Mouse3: 'Средняя кнопка мыши',
  Mouse4: 'Кнопка мыши 4',
  Mouse5: 'Кнопка мыши 5',
}

/** Подпись основной клавиши: KeyM → M, Digit1 → 1, Numpad5 → Num 5, Mouse4 → «Кнопка мыши 4». */
export function keyName(code: string): string {
  if (KEY_NAMES[code]) return KEY_NAMES[code]
  return code.replace(/^Key/, '').replace(/^Digit/, '').replace(/^Numpad/, 'Num ')
}

/** Подпись сочетания: на маке значками ⌃⌥⇧⌘ подряд, как в меню macOS, иначе Ctrl+Shift+M. */
export function formatCombo(combo: Combo | null | undefined, mac: boolean): string {
  if (!combo) return ''
  const key = keyName(combo.code)
  if (mac) return `${combo.ctrl ? '⌃' : ''}${combo.alt ? '⌥' : ''}${combo.shift ? '⇧' : ''}${combo.meta ? '⌘' : ''}${key}`
  return [combo.ctrl && 'Ctrl', combo.alt && 'Alt', combo.shift && 'Shift', combo.meta && 'Win', key].filter(Boolean).join('+')
}

/** Модификаторы, которые человек держит прямо сейчас, — подсказка в записи: «Ctrl+Shift+…». */
export function formatHeld(e: Pick<KeyLike, 'ctrlKey' | 'altKey' | 'shiftKey' | 'metaKey'>, mac: boolean): string {
  if (mac) return `${e.ctrlKey ? '⌃' : ''}${e.altKey ? '⌥' : ''}${e.shiftKey ? '⇧' : ''}${e.metaKey ? '⌘' : ''}`
  return [e.ctrlKey && 'Ctrl', e.altKey && 'Alt', e.shiftKey && 'Shift', e.metaKey && 'Win'].filter(Boolean).join('+')
}

/** Сохранённое: действие → сочетание; null — снято руками; нет ключа — по умолчанию. */
export type Bindings = Partial<Record<HotkeyAction, Combo | null>>

function validCombo(v: unknown): v is Combo {
  if (!v || typeof v !== 'object') return false
  const c = v as Record<string, unknown>
  return typeof c.code === 'string' && c.code.length > 0 && c.code.length < 40 && ['ctrl', 'alt', 'shift', 'meta'].every((k) => typeof c[k] === 'boolean') && usableCombo(c as unknown as Combo)
}

/** Разбор сохранённого: мусор и незнакомые действия отбрасываем, а не роняем приложение. */
export function parseBindings(raw: string | null): Bindings {
  if (!raw) return {}
  try {
    const data = JSON.parse(raw) as Record<string, unknown>
    const out: Bindings = {}
    for (const { id } of HOTKEY_ACTIONS) {
      if (!(id in data)) continue
      const v = data[id]
      if (v === null) out[id] = null
      else if (validCombo(v)) out[id] = { code: v.code, ctrl: v.ctrl, alt: v.alt, shift: v.shift, meta: v.meta }
    }
    return out
  } catch {
    return {}
  }
}

/** Действующие сочетания: сохранённое поверх умолчаний. */
export function effectiveBindings(saved: Bindings, mac: boolean): Record<HotkeyAction, Combo | null> {
  const out = {} as Record<HotkeyAction, Combo | null>
  for (const { id } of HOTKEY_ACTIONS) out[id] = id in saved ? (saved[id] ?? null) : defaultCombo(id, mac)
  return out
}

/** Кто ещё занимает это сочетание — при назначении его снимают с другого действия. */
export function conflictOf(bindings: Record<HotkeyAction, Combo | null>, action: HotkeyAction, combo: Combo): HotkeyAction | null {
  for (const { id } of HOTKEY_ACTIONS) {
    if (id !== action && sameCombo(bindings[id], combo)) return id
  }
  return null
}
